import { isEqual as deepIsEqual } from 'lodash';

import { Comparison, Condition, ConditionGroupOperator } from '@repo/sdk/models/components';

import { CompressorSequencerStage } from '@/features/equipment-browser/sequencer-section/compressor-sequencer/helpers';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { eaiControlledDeviceIdsAtom } from '@/state/eai-controlled-device-ids.state';

/**
 * Type-safe value conversion utilities
 */
const valueConverters = {
  toNumber: (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value);
    if (typeof value === 'boolean') return value ? 1 : 0;
    return NaN;
  },

  toString: (value: any): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  },

  isNilOrEmpty: (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  },
} as const;

/**
 * Validation utilities for different comparison types
 */
const validators = {
  isNumericComparison: (sourceValue: any, targetValue: any): boolean => {
    const sourceNum = valueConverters.toNumber(sourceValue);
    const targetNum = valueConverters.toNumber(targetValue);
    return !isNaN(sourceNum) && !isNaN(targetNum);
  },

  isStringComparison: (sourceValue: any, targetValue: any): boolean => {
    return (
      sourceValue !== null &&
      sourceValue !== undefined &&
      targetValue !== null &&
      targetValue !== undefined
    );
  },
} as const;

/**
 * Equality comparison with optional tolerance for numeric values
 */
const isEqual = (sourceValue: any, targetValue: any, tolerance?: number): boolean => {
  if (
    typeof sourceValue === 'number' &&
    typeof targetValue === 'number' &&
    tolerance !== undefined
  ) {
    return Math.abs(sourceValue - targetValue) <= tolerance;
  }

  // Deep equality check for objects
  if (typeof sourceValue === 'object' && typeof targetValue === 'object') {
    return deepIsEqual(sourceValue, targetValue);
  }
  return sourceValue === targetValue;
};

/**
 * Check if a target name exists in a sequencer object with non-zero stage values
 */
const isInSequencerObj = (sourceValue: any, targetValue: any): boolean => {
  if (!sourceValue || !sourceValue.table || !Array.isArray(sourceValue.table)) {
    return false;
  }

  if (!targetValue || typeof targetValue !== 'string') {
    return false;
  }

  const entry = sourceValue.table.find((item: any) => item.name === targetValue);

  if (!entry || !Array.isArray(entry.stageValues)) {
    return false;
  }

  return entry.stageValues.some((value: number) => value !== 0);
};

/**
 * Check if a compressor is in trim at the sequencer's current stage.
 * Looks up the compressor's row in the sequence table, then checks if
 * its cell value at the current stage column equals TRIM (3).
 *
 * @param sequenceTable - The sequence table object (from sequencerObj ref), expects { table: Array<{ name: string, stageValues: number[] }> }
 * @param compressorAlias - The compressor's device alias (from condition.value.string)
 * @param currentStage - The sequencer's current stage number (from secondaryRef / sequencer_CurrentStage), 0-indexed
 */
const isTrimAtCurrentStage = (
  sequenceTable: any,
  compressorAlias: any,
  currentStage?: any
): boolean => {
  if (!sequenceTable?.table || !Array.isArray(sequenceTable.table)) return false;
  if (!compressorAlias || typeof compressorAlias !== 'string') return false;
  if (currentStage == null) return false;

  const entry = sequenceTable.table.find((item: { name: string }) => item.name === compressorAlias);
  if (!entry?.stageValues || !Array.isArray(entry.stageValues)) return false;

  const stageIndex = Number(currentStage);
  if (isNaN(stageIndex) || stageIndex < 0 || stageIndex >= entry.stageValues.length) return false;

  return entry.stageValues[stageIndex] === CompressorSequencerStage.TRIM;
};

/**
 * Check if a device is under EAI control using jotai store
 */
const isUnderEaiControl = (_source: any, targetDeviceId: any): boolean => {
  const eaiControlledDeviceIds = jotaiStore.get(eaiControlledDeviceIdsAtom);
  return eaiControlledDeviceIds?.some((id: string) => id === targetDeviceId) ?? false;
};

/**
 * Map of comparison functions for each comparison type
 */
const comparisonHandlers: Record<Comparison, (source: any, target: any, extra?: any) => boolean> = {
  [Comparison.EqualTo]: (source: any, target: any, tolerance?: number) =>
    isEqual(source, target, tolerance),

  [Comparison.NotEqualTo]: (source: any, target: any, tolerance?: number) =>
    !isEqual(source, target, tolerance),

  [Comparison.LessThan]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) < valueConverters.toNumber(target),

  [Comparison.LessThanOrEqualTo]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) <= valueConverters.toNumber(target),

  [Comparison.GreaterThan]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) > valueConverters.toNumber(target),

  [Comparison.GreaterThanOrEqualTo]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) >= valueConverters.toNumber(target),

  [Comparison.Contains]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).includes(valueConverters.toString(target)),

  [Comparison.StartsWith]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).startsWith(valueConverters.toString(target)),

  [Comparison.EndsWith]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).endsWith(valueConverters.toString(target)),

  [Comparison.IsNilOrEmpty]: (source: any, _target?: any) => valueConverters.isNilOrEmpty(source),

  [Comparison.IsNotNilOrEmpty]: (source: any, _target?: any) =>
    !valueConverters.isNilOrEmpty(source),

  [Comparison.IsInSequencerObj]: (source: any, target?: any) => isInSequencerObj(source, target),
  [Comparison.IsUnderEaiControl]: (source: any, target?: any) => isUnderEaiControl(source, target),
  [Comparison.IsTrimAtCurrentStage]: (source: any, target: any, secondaryValue?: any) =>
    isTrimAtCurrentStage(source, target, secondaryValue),
  [Comparison.Some]: (source: any, target: any) =>
    // Currently some only supports enum arrays
    target
      .map((item: string) => item.toLowerCase())
      .some((item: string) => item === source.toLowerCase()),

  [Comparison.Every]: (source: any, target: any) =>
    // Currently every only supports enum arrays
    target
      .map((item: string) => item.toLowerCase())
      .every((item: string) => item === source.toLowerCase()),
} as const;

/**
 * Map of condition group handlers for each condition group operator
 */
export const conditionGroupHandlers: Record<
  ConditionGroupOperator,
  (conditions: boolean[]) => boolean
> = {
  [ConditionGroupOperator.LogicalAnd]: (conditions: boolean[]) => {
    return conditions.every((condition) => condition);
  },

  [ConditionGroupOperator.LogicalOr]: (conditions: boolean[]) => {
    return conditions.some((condition) => condition);
  },
};

/**
 * Performs the actual comparison based on the comparison type
 */
export const performComparison = (
  condition: Condition,
  sourceValue: any,
  targetValue: any,
  secondaryValue?: any
): boolean => {
  const { comparison, tolerance } = condition;
  const handler = comparisonHandlers[comparison as Comparison];

  if (!handler) {
    console.warn(`Unknown comparison type: ${comparison}`);
    return false;
  }

  if (comparison === Comparison.IsTrimAtCurrentStage) {
    return handler(sourceValue, targetValue, secondaryValue);
  }

  if (comparison === Comparison.IsUnderEaiControl) {
    return handler(undefined, targetValue);
  }

  // Handle special cases that don't need target values
  if (comparison === Comparison.IsNilOrEmpty || comparison === Comparison.IsNotNilOrEmpty) {
    return handler(sourceValue, undefined);
  }

  // Handle tolerance-aware comparisons
  if (
    (comparison === Comparison.EqualTo || comparison === Comparison.NotEqualTo) &&
    tolerance !== undefined
  ) {
    return handler(sourceValue, targetValue, tolerance);
  }

  return handler(sourceValue, targetValue);
};
